import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { resetPassword } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing a token. Request a new one.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true); setError("");
    try {
      const r = await resetPassword(token, password);
      setUser(r.user);
      toast.success("Password reset ✓ You're signed in.");
      navigate("/dashboard");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Reset failed. Request a new link.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[520px] mx-auto px-6 py-16" data-testid="reset-page">
      <div className="border-2 border-[#2d2a32] rounded-2xl p-8 bg-white hard-shadow">
        <div className="w-12 h-12 rounded-full border-2 border-[#2d2a32] bg-[#f7c873] flex items-center justify-center mb-4">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h1 className="serif text-4xl leading-none mb-2">Pick a new password</h1>
        <p className="text-sm text-[#5c5c5c] mb-6">
          Choose something you&#39;ll remember. Minimum 8 characters.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c5c5c]" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              data-testid="reset-password-input"
              className="w-full h-11 pl-10 pr-3 rounded-lg border-2 border-[#2d2a32] bg-white text-sm focus:outline-none focus:border-[#ff6b6b]"
              required
              minLength={8}
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c5c5c]" />
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm password"
              data-testid="reset-confirm-input"
              className="w-full h-11 pl-10 pr-3 rounded-lg border-2 border-[#2d2a32] bg-white text-sm focus:outline-none focus:border-[#ff6b6b]"
              required
              minLength={8}
            />
          </div>
          {error && <p className="text-xs text-[#ff6b6b] font-bold" data-testid="reset-error">{error}</p>}
          <button
            type="submit"
            disabled={busy || !token}
            data-testid="reset-submit"
            className="w-full h-11 rounded-full bg-[#ff6b6b] text-white font-bold text-xs tracking-[0.2em] hover:bg-[#2d2a32] hard-shadow-sm disabled:opacity-50"
          >
            {busy ? "RESETTING..." : "RESET PASSWORD"}
          </button>
        </form>
      </div>
    </div>
  );
}
